import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, OnDestroy } from '@angular/core';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, filter, finalize, map, merge, Observable, of, Subject, Subscription, switchMap, timeout } from 'rxjs';
import { NumericSortArrayByProperty, WriteOrdinalSuffixOfNumber } from 'starbase-launcher';
import { StarbaseModalButton, StarbaseModalDialogService, StarbaseModalOptions, StarbaseModalTypes } from 'starbase-modal-dialog';
import { filterWindowSlideIn } from '../animations';
import { BranchOfService } from '../models/branchOfService';
import { ImageIndividual } from '../models/imageIndividual';
import { ImageTypes } from '../models/imageType';
import { Rank } from '../models/rank';
import { RankCategory } from '../models/rankCategory';
import { Regiment } from '../models/regiment';
import { State } from '../models/state';
import { LookupDataService } from '../services/lookup-data.service';
import { UnitPickerComponent } from '../unit-picker/unit-picker.component';
import { BreakpointService, BreakpointState } from '../breakpoint.service';

const DEFAULT_DEBOUNCE_TIME = 275; // ms
const TYPEAHEAD_RESULTS_LIMIT = 5;

@Component({
  selector: 'app-individuals-search',
  templateUrl: './individuals-search.component.html',
  styleUrls: ['./individuals-search.component.css'],
  animations:  [ filterWindowSlideIn ]
})
export class IndividualsSearchComponent implements OnInit, OnDestroy {
  @Input() typeOfImageId: ImageTypes;
  @Input() data: ImageIndividual;
  @Input() rankCats: RankCategory[] = [];
  @Input() regiments: Regiment[] = [];
  @Input() states: State[] = [];
  @Input() branches: BranchOfService[] = [];
  @Input() coLetters = '';
  @Input() dropdownTypeDebounceTime = 900; // ms

  @Output() search = new EventEmitter<ImageIndividual>();
  @Output() cancel = new EventEmitter<any>();
  @Output() clear = new EventEmitter<any>();

  @ViewChild('unitPicker') unitPicker: UnitPickerComponent;
  @ViewChild('fNameSearcher') fNameSearcher: NgbTypeahead;
  @ViewChild('lNameSearcher') lNameSearcher: NgbTypeahead;
  // @ViewChild('firstFocus', { static: true }) firstFocus: ElementRef;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  ImageTypes = ImageTypes;

  public ranks: Rank[] = [];
  public ranksFiltered: Rank[] = [];
  public currentRankCat = '';
  public loaded = false;

  public allIndividualsFirstNames: string[] = [];
  public allIndividualsLastNames: string[] = [];

  writeOrdinalSuffix = WriteOrdinalSuffixOfNumber;

  ignoreTypeaheadSuggestion = false;

  fNameFocus$ = new Subject<string>();
  fNameClick$ = new Subject<string>();
  lNameFocus$ = new Subject<string>();
  lNameClick$ = new Subject<string>();

  loadedNames: boolean;
  loadedRanks: boolean;

  show = 'in'; // animate trigger

  private _subscription;
  isHandset: boolean = false;

  showIndividual: boolean;
  private rankCatUsed = -2;
  private imgIndSubscriber: Subscription;
  private ranksSubscriber: Subscription;
  constructor(private firstFocus: ElementRef,
    private cd: ChangeDetectorRef,
    private modalSvc: StarbaseModalDialogService,
    private lookupSvc: LookupDataService,
    private breakpointService: BreakpointService) { }

  ngOnInit() {

    this._subscription = this.breakpointService.handset$.subscribe(
      isHandset => {
        this.isHandset = isHandset;
      }
     )

    this.ranksSubscriber = this.lookupSvc.getAllRequisiteData<Rank>(this.lookupSvc.getRanksUrl).pipe(
      map(ranks => {
        this.ranks = ranks; Rank.setupRankCollection(this.ranks);
      })).subscribe(() => {
        this.loadedRanks = true;
        this.checkDoneLoading();
      });

    this.getTypeaheadData();

    this.regiments = NumericSortArrayByProperty(this.regiments, 'regimentNo');

    if (!this.data) {
      this.data = new ImageIndividual();
      this.data.rank = new Rank();
    }
  }

  ngOnDestroy() {
    if (this.imgIndSubscriber) {
      this.imgIndSubscriber.unsubscribe();
    }
    if (this.ranksSubscriber) {
      this.ranksSubscriber.unsubscribe();
    }
  }

  checkDoneLoading(): void {
    if (this.loaded) {
      return;
    }
    if (this.loadedNames && this.loadedRanks) {
      this.loaded = true;

      if (!this.data) {
        this.data = new ImageIndividual();
        this.reset();
      }
      this.cd.markForCheck();
      setTimeout(() => {
        this.firstFocus.nativeElement.querySelector('.focus').focus();
        // setup unit data
        if (!this.data.regimentNo) {
          this.data.regimentNo = new Regiment();
        } else {
          setTimeout(() => {
            this.unitPicker.setSelectedRegiment(this.regiments.find(r => r.uid === this.data.regimentNo.uid));
          }, 100);
        }
        if (!this.data.corps) {
          this.data.corps = null;
        } else {
          this.unitPicker.setSelectedCorps(this.data.corps);
        }
        if (!this.data.army) {
          this.data.army = 0;
        } else {
          this.unitPicker.setSelectedUnitArmy(this.data.army);
        }
        if (!this.data.state) {
          this.data.state = new State();
        } else {
          setTimeout(() => {
            this.unitPicker.setSelectedState(this.states.find(s => s.uid === this.data.state.uid));
          }, 100);
        }
        if (!this.data.branch) {
          this.data.branch = new BranchOfService();
        } else {
          setTimeout(() => {
            this.unitPicker.setSelectedBranch(this.branches.find(b => b.uid === this.data.branch.uid));
          }, 100);
        }
        if (this.data.companyLetters) {
          setTimeout(() => {
            this.unitPicker.setSelectedCoLetters(this.data.companyLetters.split(','));
          }, 100);
        }
        if (this.data.rank?.rankCategoryId) {
          this.filterRanksByCat(this.data.rank.rankCategoryId);
        }
        this.cd.markForCheck();
      }, 750);
    }
  }

  nameSearch(first: boolean = true): (text$: Observable<string>) => Observable<any[]> {
    return (text$: Observable<string>) => {
      const debouncedText$ = text$.pipe(
        debounceTime(DEFAULT_DEBOUNCE_TIME),
        distinctUntilChanged());
      const clicksWithClosedPopups$ = first
        ? this.fNameClick$.pipe(
          filter(() => !this.fNameSearcher.isPopupOpen()))
        : this.lNameClick$.pipe(filter(() => !this.lNameSearcher.isPopupOpen()));
      const inputFocus$ = first ? this.fNameFocus$ : this.lNameFocus$;
      return merge(debouncedText$, inputFocus$, clicksWithClosedPopups$).pipe(
        switchMap(term => {
          if (term.length < 2) {
            return of([]);
          } else if (first) {
            return of(this.allIndividualsFirstNames.filter(
              f => f.toLowerCase()
                .indexOf(term.toLowerCase()) > -1)
                .slice(0, TYPEAHEAD_RESULTS_LIMIT));
          } else {
            return of(this.allIndividualsLastNames.filter(
              f => f.toLowerCase()
                .indexOf(term.toLowerCase()) > -1)
                .slice(0, TYPEAHEAD_RESULTS_LIMIT));
          }
        })
      );
    };
  }

  interceptTypeaheadSelection(event: NgbTypeaheadSelectItemEvent) {
    if (this.ignoreTypeaheadSuggestion) {
      event.preventDefault();
      this.ignoreTypeaheadSuggestion = false;
    }
  }

  filterRanksByCat(rankCatId: number) {
    // eslint-disable-next-line curly
    if (!rankCatId) rankCatId = -1;
    this.ranksFiltered = []; // reset the array

    // eslint-disable-next-line eqeqeq
    this.ranksFiltered = this.ranks.filter(r => r.rankCategoryId == rankCatId);
    if (rankCatId < 0) {
      this.data.rankId = -1;
    }
    this.data.rank = new Rank();
    this.data.rank.rankCategoryId = rankCatId;
    this.rankCatUsed = rankCatId;
    this.cd.markForCheck();
  }
  updateRank(rankId: number) {
    const findRank = this.ranks.find(r => r.uid === rankId);
    if (!findRank) {
      this.data.rank = new Rank();
    } else {
      this.data.rank = new Rank().deserialize(findRank);
    }
    this.data.rank.rankCategory = new RankCategory().deserialize(this.data.rank.rankCategory);
    this.data.rank.rankCategoryId = this.data.rank.rankCategory.uid;
    this.cd.markForCheck();
  }
  updateUnit(unit) {
    if (unit === null) {
      this.data.army = null;
      this.data.corps = null;
      this.data.regimentNoId = null;
      this.data.regimentNo = null;
      this.data.stateId = null;
      this.data.state = null;
      this.data.branchId = null;
      this.data.branch = null;
      this.data.companyLetters = '';
      this.cd.markForCheck();
    } else {
      this.data.army = unit.army;
      this.data.corps = unit.corps;
      this.data.regimentNoId = (unit.regiment) ? unit.regiment.uid : null;
      this.data.regimentNo = unit.regiment;
      this.data.stateId = (unit.state) ? unit.state.uid : null;
      this.data.state = unit.state;
      this.data.branchId = (unit.branch) ? unit.branch.uid : null;
      this.data.branch = unit.branch;
      this.data.companyLetters = (unit.coLetters) ? unit.coLetters.join() : '';
      this.cd.markForCheck();
    }
  }

  runSearch() {
    this.loaded = false;
    this.data.rankId = (this.data.rankId && this.data.rankId !== 0) ? this.data.rankId : -1;
    this.data.regimentNo = (this.data.regimentNo && this.data.regimentNo.uid !== 0) ? this.data.regimentNo : null;
    this.data.state = (this.data.state && this.data.state.uid !== 0) ? this.data.state : null;
    this.data.branch = (this.data.branch && this.data.branch.uid !== 0) ? this.data.branch : null;
    this.data.companyLetters = (this.data.companyLetters === '*') ? '' : this.data.companyLetters;

    this.search.emit(this.data);
  }

  reset(emitReset = false) {
    // this.data = new ImageIndividual();
    this.data.firstName = '';
    this.data.lastName = '';
    this.data.currentRankInPhoto = false;
    this.data.notes = '';
    Rank.setupRankCollection(this.ranks);
    this.data.rank = new Rank();
    this.data.rankId = null;
    this.data.rank.rankCategory = new RankCategory();
    this.data.rank.rankCategoryId = -1;
    this.data.regimentNo = new Regiment();
    this.data.state = new State();
    this.data.branch = new BranchOfService();
    this.data.companyLetters = '';
    this.data.army = 0;
    this.data.corps = null;
    this.ranksFiltered = this.ranks;
    this.cd.markForCheck();
    if (emitReset) {
      this.clear.emit();
    }
  }

  close(): void {
    this.showIndividual = false;
    this.loaded = false;
    this.loadedRanks = false;
    this.loadedNames = false;
    this.cancel.emit();
  }

  private resetRankToNone(): void {
    this.data.rank = new Rank();
    this.data.rankId = null;
    this.data.rank.rankCategory = new RankCategory();
    this.data.rank.rankCategoryId = -1;
  }

  // server must respond within 22 seconds, or timeout error occurs and component will load without typeahead support.
  private getTypeaheadData() {
    this.imgIndSubscriber = this.lookupSvc.getAllImageIndividualsFromServer().pipe(
      timeout(22000),
      finalize(() => {
      this.loadedNames = true;
      this.checkDoneLoading();
    })).subscribe(i => {
        i.map(ii => {
          this.allIndividualsFirstNames.push(ii.firstName);
          this.allIndividualsLastNames.push(ii.lastName);
        });
      },
      () => {
        console.error('Could not load individuals name data.');
      });
  }

}
